import { motion } from 'framer-motion';
import { ChevronDown, FileText, CheckCircle, XCircle, X } from 'lucide-react';

export const Input = ({ label, required, ...props }) => (
  <div className="flex flex-col space-y-2">
    {label && (
      <label className="label">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
    )}
    <input className="input-field" {...props} />
  </div>
);

export const Select = ({ label, children, ...props }) => (
  <div className="flex flex-col space-y-2">
    {label && <label className="label">{label}</label>}
    <select className="input-field bg-white" {...props}>
      {children}
    </select>
  </div>
);

export const StatCard = ({ title, value, icon, color, subtitle, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ y: -4 }}
    transition={{ duration: 0.2 }}
    className="card-hover p-6"
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
        <p className="text-2xl font-bold font-heading tracking-tight">{value}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-xl ${color}`}>{icon}</div>
    </div>
  </motion.div>
);

export const Badge = ({ type }) => {
  const styles = {
    invoice: 'badge badge-invoice',
    quote: 'badge badge-quote',
    po: 'badge badge-po',
    sj: 'badge badge-sj',
  };
  const labels = { invoice: 'Invoice', quote: 'Quote', po: 'PO', sj: 'Surat Jalan' };
  return (
    <span className={styles[type] || 'badge bg-slate-100 text-slate-700'}>
      {labels[type] || type}
    </span>
  );
};

export const StatusBadge = ({ status }) => {
  const styles = {
    paid: 'badge badge-paid',
    sent: 'badge badge-pending',
    pending: 'badge badge-pending',
    draft: 'badge badge-draft',
    converted: 'badge badge-paid',
    cancelled: 'badge bg-slate-100 text-slate-500 border-slate-200',
  };
  const labels = {
    paid: 'Lunas',
    sent: 'Terkirim',
    pending: 'Pending',
    draft: 'Draft',
    converted: 'Dikonversi',
    cancelled: 'Dibatalkan',
  };
  return <span className={styles[status] || 'badge badge-draft'}>{labels[status] || status}</span>;
};

export const NavItem = ({ icon, label, active, onClick, hasSubmenu, isOpen, testId }) => (
  <button onClick={onClick} className={`nav-item ${active ? 'active' : ''}`} data-testid={testId}>
    <span className="flex items-center gap-3">
      {icon}
      <span className="font-medium">{label}</span>
    </span>
    {hasSubmenu && (
      <motion.span animate={{ rotate: isOpen ? 180 : 0 }}>
        <ChevronDown size={16} />
      </motion.span>
    )}
  </button>
);

export const SubNavItem = ({ label, active, onClick, icon, testId }) => (
  <button onClick={onClick} className={`nav-item pl-12 ${active ? 'active' : ''}`} data-testid={testId}>
    {icon && <span className="w-4">{icon}</span>}
    <span>{label}</span>
  </button>
);

export const EmptyState = ({ title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
      <FileText size={24} className="text-slate-400" />
    </div>
    <h3 className="text-lg font-heading font-semibold mb-2">{title}</h3>
    <p className="text-muted-foreground mb-6 max-w-md text-sm">{description}</p>
    {action}
  </div>
);

export const Toast = ({ message, type, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: 50, scale: 0.9 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: 50, scale: 0.9 }}
    className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-4 rounded-xl shadow-dropdown ${
      type === 'success' ? 'bg-emerald-600 text-white' : 'text-white'
    }`}
    style={{
      backgroundColor: type === 'error' ? '#F62947' : type !== 'success' ? '#212529' : undefined,
    }}
  >
    {type === 'success' && <CheckCircle size={20} />}
    {type === 'error' && <XCircle size={20} />}
    <span className="font-medium">{message}</span>
    <button onClick={onClose} className="ml-2 hover:opacity-70">
      <X size={18} />
    </button>
  </motion.div>
);
